import { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Check, ChevronRight } from 'lucide-react';
import {
  useCountries,
  useDepartments,
  usePositions,
  useContractTypes,
  useCreateEmployee,
  useUpdateEmployee,
  useEmployee,
} from '@/lib/queries';

const STEPS = ['Datos Generales', 'Contacto', 'Perfil del Puesto'];

export function EmployeeFormPage() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { data: existing } = useEmployee(id);
  const { data: countries } = useCountries();
  const { data: departments } = useDepartments();
  const { data: positions } = usePositions();
  const create = useCreateEmployee();
  const update = useUpdateEmployee();
  const [step, setStep] = useState(0);

  const [form, setForm] = useState<any>(
    existing ?? {
      employeeNumber: '',
      firstName: '',
      lastName: '',
      identification: '',
      identificationType: 'DPI',
      corporateEmail: '',
      hireDate: new Date().toISOString().slice(0, 10),
      countryId: '',
      positionId: '',
      departmentId: '',
      contractTypeId: '',
      workMode: 'PRESENCIAL',
    },
  );
  const { data: contractTypes } = useContractTypes(form.countryId);
  const [error, setError] = useState<string | null>(null);

  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  const submit = async () => {
    setError(null);
    try {
      if (isEdit) await update.mutateAsync({ id: id!, payload: form });
      else await create.mutateAsync(form);
      navigate('/colaboradores');
    } catch (err: any) {
      setError(err.response?.data?.error?.message ?? 'Error');
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <Link
        to="/colaboradores"
        className="inline-flex items-center gap-2 text-sm font-medium text-font-secondary hover:text-primary"
      >
        <ArrowLeft size={16} /> Volver
      </Link>
      <h1 className="text-2xl font-bold text-font-primary">
        {isEdit ? 'Editar colaborador' : 'Nuevo colaborador'}
      </h1>

      {/* Step breadcrumb */}
      <div className="rounded-lg border border-border bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2">
          {STEPS.map((s, i) => {
            const isDone = i < step;
            const isCurrent = i === step;
            return (
              <div key={s} className="flex items-center gap-2">
                <button
                  onClick={() => setStep(i)}
                  className={`flex items-center gap-2 rounded-md px-3 py-2 text-xs font-semibold transition ${
                    isCurrent
                      ? 'bg-primary-subtle text-primary'
                      : isDone
                      ? 'text-accent'
                      : 'text-font-tertiary'
                  }`}
                >
                  <span
                    className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] ${
                      isCurrent
                        ? 'bg-primary text-white'
                        : isDone
                        ? 'bg-accent text-white'
                        : 'bg-border text-font-secondary'
                    }`}
                  >
                    {isDone ? <Check size={12} /> : i + 1}
                  </span>
                  {s}
                </button>
                {i < STEPS.length - 1 && (
                  <ChevronRight size={14} className="text-font-tertiary" />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-lg border border-border bg-white p-6 shadow-sm">
        {step === 0 && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="# Empleado" required>
              <input
                className="input h-10"
                value={form.employeeNumber}
                onChange={(e) => set('employeeNumber', e.target.value)}
              />
            </Field>
            <Field label="Identificación (DPI/cédula)" required>
              <input
                className="input h-10"
                value={form.identification}
                onChange={(e) => set('identification', e.target.value)}
              />
            </Field>
            <Field label="Nombres" required>
              <input
                className="input h-10"
                value={form.firstName}
                onChange={(e) => set('firstName', e.target.value)}
              />
            </Field>
            <Field label="Apellidos" required>
              <input
                className="input h-10"
                value={form.lastName}
                onChange={(e) => set('lastName', e.target.value)}
              />
            </Field>
            <Field label="Fecha de nacimiento">
              <input
                type="date"
                className="input h-10"
                value={form.birthDate ?? ''}
                onChange={(e) => set('birthDate', e.target.value)}
              />
            </Field>
            <Field label="Fecha de ingreso" required>
              <input
                type="date"
                className="input h-10"
                value={form.hireDate}
                onChange={(e) => set('hireDate', e.target.value)}
              />
            </Field>
            <Field label="País" required>
              <select
                className="input h-10"
                value={form.countryId}
                onChange={(e) => set('countryId', e.target.value)}
              >
                <option value="">Selecciona un país</option>
                {countries?.items?.map((c: any) => (
                  <option key={c.id} value={c.id}>
                    {c.nameEs}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Departamento" required>
              <select
                className="input h-10"
                value={form.departmentId}
                onChange={(e) => set('departmentId', e.target.value)}
              >
                <option value="">Selecciona departamento</option>
                {departments?.items?.map((d: any) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Puesto" required>
              <select
                className="input h-10"
                value={form.positionId}
                onChange={(e) => set('positionId', e.target.value)}
              >
                <option value="">Selecciona puesto</option>
                {positions?.items?.map((p: any) => (
                  <option key={p.id} value={p.id}>
                    {p.title}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Tipo de contrato" required>
              <select
                className="input h-10"
                value={form.contractTypeId}
                onChange={(e) => set('contractTypeId', e.target.value)}
              >
                <option value="">Selecciona contrato</option>
                {contractTypes?.items?.map((c: any) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        )}

        {step === 1 && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Correo corporativo" required>
              <input
                type="email"
                className="input h-10"
                value={form.corporateEmail}
                onChange={(e) => set('corporateEmail', e.target.value)}
              />
            </Field>
            <Field label="Correo personal">
              <input
                type="email"
                className="input h-10"
                value={form.personalEmail ?? ''}
                onChange={(e) => set('personalEmail', e.target.value)}
              />
            </Field>
            <Field label="Teléfono">
              <input
                className="input h-10"
                value={form.phone ?? ''}
                onChange={(e) => set('phone', e.target.value)}
              />
            </Field>
            <Field label="Dirección">
              <input
                className="input h-10"
                value={form.address ?? ''}
                onChange={(e) => set('address', e.target.value)}
              />
            </Field>
            <Field label="Contacto de emergencia">
              <input
                className="input h-10"
                value={form.emergencyContact ?? ''}
                onChange={(e) => set('emergencyContact', e.target.value)}
              />
            </Field>
            <Field label="Teléfono de emergencia">
              <input
                className="input h-10"
                value={form.emergencyPhone ?? ''}
                onChange={(e) => set('emergencyPhone', e.target.value)}
              />
            </Field>
          </div>
        )}

        {step === 2 && (
          <div className="flex flex-col gap-4">
            <Field label="Función principal">
              <textarea
                rows={3}
                className="input"
                value={form.mainFunction ?? ''}
                onChange={(e) => set('mainFunction', e.target.value)}
              />
            </Field>
            {[1, 2, 3].map((n) => (
              <Field key={n} label={`Responsabilidad ${n}`}>
                <input
                  className="input h-10"
                  value={form[`responsibility${n}`] ?? ''}
                  onChange={(e) => set(`responsibility${n}`, e.target.value)}
                />
              </Field>
            ))}
          </div>
        )}

        {step === STEPS.length - 1 && (
          <p className="mt-6 rounded-md border border-dashed border-border bg-bg p-4 text-xs text-font-secondary">
            Tras crear al colaborador podrás cargar sus documentos, evaluaciones y
            datos de cumplimiento desde la vista de detalle.
          </p>
        )}

        {error && <p className="mt-4 text-sm text-error">{error}</p>}

        <div className="mt-8 flex justify-between border-t border-border pt-5">
          <button
            type="button"
            onClick={() => (step > 0 ? setStep(step - 1) : navigate('/colaboradores'))}
            className="flex h-10 items-center gap-2 rounded-md border border-border bg-white px-4 text-sm font-semibold text-font-primary hover:bg-bg"
          >
            {step === 0 ? 'Cancelar' : 'Anterior'}
          </button>
          {step < STEPS.length - 1 ? (
            <button
              type="button"
              onClick={() => setStep(step + 1)}
              className="flex h-10 items-center gap-2 rounded-md bg-primary px-4 text-sm font-semibold text-white hover:bg-primary-dark"
            >
              Siguiente <ChevronRight size={14} />
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={create.isPending || update.isPending}
              className="flex h-10 items-center gap-2 rounded-md bg-primary px-4 text-sm font-semibold text-white hover:bg-primary-dark disabled:opacity-60"
            >
              {isEdit ? 'Guardar cambios' : 'Crear colaborador'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold text-font-primary">
        {label} {required && <span className="text-error">*</span>}
      </span>
      {children}
    </label>
  );
}
